import { Component, input, output, inject, signal, computed, effect, OnInit, OnDestroy } from '@angular/core';

import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, ActivatedRoute } from '@angular/router';
import { MonitorsService } from '../../services/monitors.service';
import { Monitor } from '../../models/monitor.model';

@Component({
  selector: 'app-monitors-table',
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule
  ],
  templateUrl: './monitors-table.component.html',
  styleUrls: ['./monitors-table.component.scss'],
})
export class MonitorsTableComponent implements OnInit, OnDestroy {
  isSitrackAdministrator = input(false);
  isAdministrator = input(false);
  dataSource = input<Monitor[]>([]);
  logoutClicked = output<void>();
  openMonitorConfigClicked = output<void>();
  openUsersConfigClicked = output<void>();
  monitorSelected = output<{
    id: string;
    name: string;
  } | null>();

  displayedColumns: string[] = ['name'];

  configItems = computed(() => {
    const items: any[] = [
      {
        icon: 'settings',
        text: 'Configuración de monitores',
        action: 'config',
        class: 'config_moitors'
      }
    ];

    if (this.isSitrackAdministrator()) {
      items.push({
        icon: 'admin_panel_settings',
        text: 'Administración',
        action: 'config',
        class: 'config_moitors'
      });
    }

    items.push({
      icon: 'arrow_back_24',
      text: 'Cerrar Sección',
      action: 'logout',
      class: 'logout_section'
    });

    return items;
  });

  monitor = signal<Monitor[]>([]);

  isLoading = signal(false);

  error = signal<string | null>(null);

  selectedMonitorId = signal<string | null>(null);

  showDetailsSidebar = signal(false);

  currentMonitorId = signal<string | null>(null);
  currentMonitorName = signal<string | null>(null);

  private requestedMonitorId: string | null = null;
  private selectionRedirectTimer: any = null;

  private monitorsService = inject(MonitorsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private _dataSourceWatcher = effect(() => {
    const ds = this.dataSource();
    if (ds.length > 0) {
      this.monitor.set(ds);
      this.trySelectRequestedMonitor();
    }
  });

  ngOnInit(): void {
    const idFromRoute = this.route.snapshot.paramMap.get('monitorId') || this.route.snapshot.paramMap.get('id');
    if (idFromRoute) {
      this.requestedMonitorId = String(idFromRoute);
      this.trySelectRequestedMonitor();
    }
  }

  ngOnDestroy(): void {
    if (this.selectionRedirectTimer) {
      clearTimeout(this.selectionRedirectTimer);
      this.selectionRedirectTimer = null;
    }
  }

  private trySelectRequestedMonitor(): void {
    if (!this.requestedMonitorId) return;

    if (this.monitor() && this.monitor().length > 0) {
      const found = this.monitor().find(m => String(m.id) === String(this.requestedMonitorId));
      if (found) {
        this.selectMonitor(found);
        this.requestedMonitorId = null;
        if (this.selectionRedirectTimer) { clearTimeout(this.selectionRedirectTimer); this.selectionRedirectTimer = null; }
        return;
      }
    }

    this.monitorsService.getMonitor(this.requestedMonitorId).subscribe({
      next: (resp: any) => {
        if (resp && resp.id) {
          this.requestedMonitorId = null;
          this.monitor.update(current => {
            const existingIdx = current.findIndex(m => String(m.id) === String(resp.id));
            if (existingIdx >= 0) {
              const updated = [...current];
              updated[existingIdx] = resp;
              return updated;
            } else {
              return [resp as Monitor, ...current];
            }
          });
          this.selectMonitor(resp as Monitor);
          try { this.monitorsService.monitorUpdated.next(resp); } catch (e) { /* ignore */ }
          if (this.selectionRedirectTimer) { clearTimeout(this.selectionRedirectTimer); this.selectionRedirectTimer = null; }
        }
      },
      error: () => {
        // dejar que el timer original redirija si está configurado
      }
    });
  }

  trackByMonitorId(index: number, item: Monitor): string {
    return item.id || index.toString();
  }

  isSelected(monitor: Monitor): boolean {
    return this.selectedMonitorId() === monitor.id;
  }

  toggleDetailsSidebar(): void {
    this.showDetailsSidebar.update(v => !v);
  }

  selectMonitor(monitor: Monitor): void {
    if (String(this.selectedMonitorId()) === String(monitor.id)) {
      return;
    }
    
    console.log("Selected monitor:", monitor);
    this.selectedMonitorId.set(String(monitor.id));
    this.currentMonitorId.set(String(monitor.id));
    this.currentMonitorName.set(monitor.name);

    this.monitorSelected.emit({
      id: monitor.id || '',
      name: monitor.name,
    });

    try {
      this.monitorsService.monitorUpdated.next(monitor);
    } catch (e) {
      // ignore
    }
  }

  onLogoutClick(): void {
    this.logoutClicked.emit();
  }

  onOpenMonitorConfigClicked(): void {
    this.openMonitorConfigClicked.emit();
  }

  onOpenDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  goToDashboard(monitor: Monitor): void {
    if (!monitor || !monitor.id) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.router.navigate(['/dashboard', monitor.id]);
  }

  onOpenUsersConfigClicked(): void {
    this.openUsersConfigClicked.emit();
  }

  goToAdministracion(): void {
    this.router.navigate(['/sitrack']);
  }
}
